/**
 * B397 leg (2) — the tickets step as ONE card (redline-storefront-tickets-390.md,
 * 72:2): travel day, departure and ticket counts all visible at once,
 * with the sticky CONTINUE carrying the running total. The SLOT_FIRST /
 * TICKETS_FIRST sequencing dissolves on this face — the card holds both
 * choices, and the frame's own flow facts govern: changing day re-derives
 * departures, changing departure KEEPS counts (state lives in the
 * container's composition, not in this component).
 *
 * Honesty seams, each from a read or absent (§10):
 *  - seats-left comes from the availability map; no map entry = no note,
 *    never an invented count. Low inventory turns the note AMBER (72:2's
 *    #ffb224); sold out disables the pill — the frame does not draw a
 *    sold-out shape, and a disabled pill with the truthful note is the
 *    honest one.
 *  - the footnote's accompaniment sentence exists only where THIS card's
 *    catalog carries the rule; the teaching half ("the total updates as
 *    you go") always stands.
 *  - the CONTINUE total is the sum of the composed lines' DISPLAY prices
 *    (§10.1 display); a composition touching an unpriced type drops the
 *    figure from the door rather than summing a guess — the platform's
 *    own total lands at the cart.
 *  - the shut door teaches (R14/B285: ONE statement): the summary slot
 *    above the bar says what is still missing while the door is shut, and
 *    restates the selection once it is armed.
 */
import type { TimeFormat } from "@transact/ui";
import { clockWords } from "../lib/when.js";
import type { ReactElement } from "react";
import { Button, MonthCalendar, PhotoCarousel, PickerRail, PickerTile, SheetCard, SheetFoot, StickyBar, Stepper as UiStepper, TotalRow, cap } from "@transact/ui";
import { calendarMonth, pageDescription, pagePhotos, shortDayWords, type ProductMedia } from "../lib/productPage.js";
import { formatMoney } from "../lib/money.js";
import { groupDepartures, seatsLeft, type DisplayOptions } from "./departures.js";
import { browseAllDoor, emptyDepartureFace } from "./screens.js";
import { EmptyState } from "@transact/ui";
import type { PoolAvailability, ProductSummary, SlotSummary } from "./screens.js";
import {
  compositionIssues,
  compositionLines,
  issueHint,
  quantityOf,
  type Composition,
} from "../lib/composition.js";
import { orderCap, totalQuantity } from "../lib/composition.js";
import { accompanimentNote, cardMeta, plainName, plural, type BuyerVocabulary } from "../lib/bookingFace.js";
import { needsDepartureStep } from "../lib/browse.js";
import { capDoorLabel, capRowHint, groupFooterLine, groupParty, keepLabel, reduceToKeep, stepperMax } from "../lib/groupBooking.js";
import { GroupBookingPanel } from "./groupBooking.js";

const LOW_SEATS = 5;

/** The composed party in buyer words: "2 adults, 1 child". Types with 0 stay unsaid.
 * B403 (72:2's picks line): the qualifier drops ("Child (2–12)" → child) and
 * plurals ride the shared buyer-copy derivation (child → children). */
export function partyWords(card: ProductSummary, comp: Composition): string | undefined {
  const parts: string[] = [];
  for (const v of card.variants) {
    const q = quantityOf(comp, v.variantId);
    if (q === 0) continue;
    const noun = plainName(v.name);
    parts.push(`${q} ${q === 1 ? noun : plural(noun)}`);
  }
  return parts.length === 0 ? undefined : parts.join(", ");
}

/** The display-price sum of the composed lines; undefined when any composed type is unpriced. */
export function displayTotal(card: ProductSummary, comp: Composition): { readonly amount: number; readonly currency: string } | undefined {
  const lines = compositionLines(card, comp);
  if (lines.length === 0) return undefined;
  let sum = 0;
  let currency: string | undefined;
  for (const line of lines) {
    if (line.linePrice === undefined) return undefined;
    sum += line.linePrice.amount;
    currency = line.linePrice.currency;
  }
  return currency === undefined ? undefined : { amount: sum, currency };
}

export function TicketsStep(props: {
  /** B523 — which commit bar the step wears: 72:2's bar (default) or the
   * dialog sheet's foot (368-2: total left, the one door right). */
  readonly foot?: "bar" | "sheet";
  readonly card: ProductSummary;
  readonly slots: readonly SlotSummary[];
  readonly availability: Readonly<Record<string, readonly PoolAvailability[]>>;
  readonly comp: Composition;
  readonly onChange: (variantId: string, quantity: number) => void;
  readonly chosenSlotId?: string;
  readonly onPickSlot: (slotId: string) => void;
  readonly selectedDayKey?: string;
  readonly onSelectDay: (key: string) => void;
  readonly onContinue: () => void;
  /** B345 — the way back to the catalog, drawn on the no-departures refusal. */
  readonly onBrowseAll?: () => void;
  readonly busy?: boolean;
  readonly locale?: string;
  readonly timeZone?: string;
  /** B621 — the tenant's clock; absent = 12h AM/PM, the ruled default. */
  readonly timeFormat?: TimeFormat;
  /** B403 #22-extension — the tenant's buyer vocabulary (the mint's tenantType); absent keeps the standing register. */
  readonly vocab?: BuyerVocabulary;
  /** B521 — the product read's photos + whole description; absent = the card's own photo and first sentence. */
  readonly media?: ProductMedia;
  /** B549 — the channel's group threshold (the mint's wire fact); absent = no interrupt. */
  readonly groupThreshold?: number | undefined;
  /** B549 — the tenant's name, for the panel's blurb. */
  readonly businessName?: string | undefined;
  /** B549 — the interrupt's primary: walk into the inquiry form. */
  readonly onRequestGroupQuote?: (() => void) | undefined;
  /** B549 — "or keep 19 tickets": step the party back under the threshold. */
  readonly onKeepTickets?: (() => void) | undefined;
}): ReactElement {
  const opts: DisplayOptions = {
    ...(props.locale !== undefined ? { locale: props.locale } : {}),
    ...(props.timeZone !== undefined ? { timeZone: props.timeZone } : {}),
    ...(props.timeFormat !== undefined ? { timeFormat: props.timeFormat } : {}),
  };
  const scheduled = needsDepartureStep({ productId: props.card.productId, scheduleIds: props.card.scheduleIds });
  const groups = groupDepartures(props.slots, opts);
  if (scheduled && groups.length === 0) {
    /* B345's face, unchanged: nothing ON_SALE has no date to point at. */
    const face = emptyDepartureFace(props.slots);
    return <EmptyState title={face.title} body={face.body} action={browseAllDoor(props.onBrowseAll)} />;
  }
  const selectedDay = groups.find((g) => g.key === props.selectedDayKey) ?? groups[0];
  /* B403 wave-2 residual — the frames' 24h register ("10:30", "07:00"),
     one derivation for the whole storefront (lib/when.ts). */
  const timeFmt = { format: (ms: number) => clockWords(ms, opts) };
  const chosen = props.slots.find((s) => s.slotId === props.chosenSlotId);
  /* a chosen departure that is not on the selected day is still CHOSEN —
     72:2: changing day re-derives departures, changing departure keeps counts */
  const issues = compositionIssues(props.card, props.comp);
  const ticketCount = totalQuantity(props.comp);
  /* B549 (444:2) — at the threshold the calendar column becomes the GROUP
     BOOKING panel and the foot offers the desk; the steppers can REACH the
     threshold but never pass it (the cart wall would refuse the add). */
  /* B580 — or one past a type's own "Most per order", while groups are on:
     that press is the groups door, and the panel speaks cap + 1. */
  const group = props.onRequestGroupQuote !== undefined ? groupParty(props.card, props.comp, props.groupThreshold) : undefined;
  const interrupt = group !== undefined;
  const kept = group !== undefined && props.groupThreshold !== undefined ? totalQuantity(reduceToKeep(props.comp, props.groupThreshold, props.card)) : 0;
  /* B580 — a row standing AT its cap while that cap opens the desk. */
  const capDoor = (v: ProductSummary["variants"][number]): number | undefined => {
    const cap = v.constraints?.maxPerOrder;
    return props.onRequestGroupQuote !== undefined &&
      cap !== undefined &&
      quantityOf(props.comp, v.variantId) === cap &&
      orderCap(props.card, v.variantId, props.groupThreshold) > cap
      ? cap
      : undefined;
  };
  const meta = cardMeta(props.card);
  const rule = props.card.variants.some((v) => v.constraints?.requiresVariantId !== undefined);
  const total = displayTotal(props.card, props.comp);

  const missing: string[] = [];
  if (ticketCount === 0) missing.push("tickets");
  if (scheduled && selectedDay !== undefined && props.chosenSlotId === undefined)
    /* B403 #39-b — the gating sentence is a buyer sentence (#22-extension): the tenant's word. */
    missing.push(`a ${props.vocab?.departureNoun.singular ?? "departure"}`);
  const gated = missing.length > 0 || issues.length > 0;
  const party = partyWords(props.card, props.comp);
  const summary = gated
    ? issues.length > 0
      ? undefined /* the §4.2 hints above already state it — one statement (B285) */
      : `Pick ${missing.join(" and ")} to continue.`
    : [party, chosen !== undefined ? `${selectedDay !== undefined && groups.length > 0 ? `${selectedDay.weekday} ${selectedDay.dayOfMonth} · ` : ""}${timeFmt.format(chosen.start)}` : undefined]
        .filter((p): p is string => p !== undefined)
        .join(" · ");

  /* B521 — the page's photos and words: the product read's, else the card's. */
  const photos = pagePhotos(props.card, props.media);
  const description = pageDescription(props.card, props.media);
  const month = scheduled ? calendarMonth(props.card, groups, selectedDay?.key, props.locale) : undefined;
  const when = chosen !== undefined ? `${shortDayWords(chosen.start, opts)} · ${timeFmt.format(chosen.start)}` : undefined;
  const pickLines = props.card.variants
    .map((v) => ({ v, qty: quantityOf(props.comp, v.variantId) }))
    .filter(({ qty }) => qty > 0)
    .map(({ v, qty }) => ({
      key: v.variantId,
      words: [`${qty} × ${v.name}`, ...(when !== undefined ? [when] : [])].join(" · "),
      ...(v.displayPrice !== undefined
        ? { amount: formatMoney({ amount: v.displayPrice.amount * qty, currency: v.displayPrice.currency }) }
        : {}),
    }));

  return (
    <div className="tickets-step product-page">
      <SheetCard redline="tickets-card">
        {photos.length > 0 ? <PhotoCarousel subject={props.card.name} photos={photos} redline="product-photos" /> : null}
        <header className="tickets-head">
          <h2 className="tickets-title">{props.card.name}</h2>
          {meta !== undefined ? <p className="tickets-meta">{meta}</p> : null}
        </header>
        {description !== undefined ? (
          <p className="product-description" data-redline="product-description">
            {description}
          </p>
        ) : null}

        {scheduled ? (
          <div className="tickets-narrow">
            <section aria-labelledby="tickets-day-label">
              <p className="tickets-label" id="tickets-day-label" data-redline="tickets-day-label">
                Travel day
              </p>
              {/* review #1 (2b): a season of days is a WINDOW, not a stack */}
              <PickerRail describedBy="tickets-day-label" window>
                {groups.map((day) => (
                  <li key={day.key}>
                    <PickerTile
                      compact
                      kicker={day.weekday}
                      value={day.dayOfMonth}
                      label={day.label}
                      pressed={day.key === selectedDay?.key}
                      onPress={() => props.onSelectDay(day.key)}
                    />
                  </li>
                ))}
              </PickerRail>
            </section>

            <section aria-labelledby="tickets-departure-label">
              {/* B403 (#39): the picker's label speaks the tenant's word —
                  a ferry sells sailings, not departures. */}
              <p className="tickets-label" id="tickets-departure-label">
                {props.vocab === undefined ? "Departure" : cap(props.vocab.departureNoun.singular)}
              </p>
              <PickerRail describedBy="tickets-departure-label">
                {(selectedDay?.departures ?? []).map((d) => {
                  const left = seatsLeft(props.availability[d.slotId] ?? []);
                  const soldOut = left !== undefined && left <= 0;
                  const low = left !== undefined && left > 0 && left <= LOW_SEATS;
                  return (
                    <li key={d.slotId}>
                      <PickerTile
                        compact
                        value={timeFmt.format(d.start)}
                        {...(left !== undefined
                          ? soldOut
                            ? { note: "Sold out", noteTone: "low" as const }
                            : { note: `${left} left`, ...(low ? { noteTone: "warn" as const } : {}) }
                          : {})}
                        pressed={d.slotId === props.chosenSlotId}
                        disabled={soldOut}
                        onPress={() => props.onPickSlot(d.slotId)}
                      />
                    </li>
                  );
                })}
              </PickerRail>
            </section>
          </div>
        ) : null}

        <section aria-labelledby="tickets-types-label">
          <p className="tickets-label" id="tickets-types-label">
            Tickets
          </p>
          <ul className="tickets-rows">
            {props.card.variants.map((v, i) => (
              <li key={v.variantId} className="tickets-row">
                <span className="tickets-row-name" data-redline={`embed-group-interrupt-class-${i + 1}`}>{v.name}</span>
                {v.displayPrice !== undefined ? (
                  <span className="tickets-row-price">{formatMoney(v.displayPrice)}</span>
                ) : null}
                <UiStepper
                  frame="sheet"
                  redline={`embed-group-interrupt-stepper-${i + 1}`}
                  valueRedline={`embed-group-interrupt-stepper-${i + 1}-count`}
                  label={v.name}
                  value={quantityOf(props.comp, v.variantId)}
                  min={0}
                  max={stepperMax(props.card, v.variantId, props.groupThreshold, ticketCount, quantityOf(props.comp, v.variantId))}
                  onChange={(next) => props.onChange(v.variantId, next)}
                  decreaseLabel={`Fewer ${v.name}`}
                  increaseLabel={capDoor(v) !== undefined ? capDoorLabel(capDoor(v)!) : `More ${v.name}`}
                />
                {!interrupt && capDoor(v) !== undefined ? <p className="hint tickets-row-cap" role="status">{capRowHint(capDoor(v)!, v.name)}</p> : null}
              </li>
            ))}
          </ul>
        </section>

        {issues.length > 0 ? (
          <div id="tickets-issues">
            {issues.map((issue) => (
              <p key={`${issue.kind}:${issue.variantId}`} className="hint" role="status">
                {issueHint(issue, props.card)}
              </p>
            ))}
          </div>
        ) : null}

        {/* B403 (72:2, the copy wall's first catch): the rule sentence
            speaks the catalog's OWN types through the shared accompaniment
            derivation — "Children and bikes travel with an adult on the
            same sailing" in dev-ferry's own words (#22-extension) — never
            a genericized "some tickets". */}
        <p className="tickets-footnote">
          {(() => {
            const note = rule ? accompanimentNote([props.card], props.vocab) : undefined;
            return note !== undefined
              ? `${note.slice(0, -1)} — the total updates as you go.`
              : "The total updates as you go.";
          })()}
        </p>
      </SheetCard>

      {/* B521 (364-2) — the wide face's second column: the month, the
          departures of the chosen day, and what the buyer has picked. The
          narrow face (72:2) keeps its rails inside the one card above; each
          face hides the other's copy, so one state drives both. */}
      <aside className="product-side tickets-wide" aria-label={interrupt ? "Group booking" : "Your booking"}>
        {group !== undefined ? (
          <GroupBookingPanel threshold={group.size} businessName={props.businessName} cap={group.cap} />
        ) : (
        <SheetCard redline="product-side">
          {scheduled && month !== undefined ? (
            <MonthCalendar
              kicker="Pick a day"
              title={month.title}
              leadingBlanks={month.leadingBlanks}
              days={month.days}
              {...(selectedDay !== undefined ? { selectedKey: selectedDay.key } : {})}
              onSelect={props.onSelectDay}
              {...(month.prevKey !== undefined ? { onPrevMonth: () => props.onSelectDay(month.prevKey as string) } : {})}
              {...(month.nextKey !== undefined ? { onNextMonth: () => props.onSelectDay(month.nextKey as string) } : {})}
              {...(month.legend !== undefined ? { legend: month.legend } : {})}
              redline="product-calendar"
            />
          ) : null}
          {scheduled && selectedDay !== undefined ? (
            <section aria-labelledby="product-departure-label">
              <p className="tickets-label" id="product-departure-label">
                {`${props.vocab === undefined ? "Departure" : cap(props.vocab.departureNoun.singular)} · ${selectedDay.weekday} ${selectedDay.dayOfMonth}`}
              </p>
              <PickerRail describedBy="product-departure-label">
                {selectedDay.departures.map((d) => {
                  const left = seatsLeft(props.availability[d.slotId] ?? []);
                  const soldOut = left !== undefined && left <= 0;
                  const low = left !== undefined && left > 0 && left <= LOW_SEATS;
                  return (
                    <li key={d.slotId}>
                      <PickerTile
                        compact
                        value={timeFmt.format(d.start)}
                        {...(left !== undefined
                          ? soldOut
                            ? { note: "Sold out", noteTone: "low" as const }
                            : { note: `${left} left`, ...(low ? { noteTone: "warn" as const } : {}) }
                          : {})}
                        pressed={d.slotId === props.chosenSlotId}
                        disabled={soldOut}
                        onPress={() => props.onPickSlot(d.slotId)}
                      />
                    </li>
                  );
                })}
              </PickerRail>
            </section>
          ) : null}
          <section aria-labelledby="product-picks-label" className="product-picks">
            <p className="tickets-label" id="product-picks-label">
              Your picks
            </p>
            {pickLines.length === 0 ? (
              <p className="product-picks-empty">No tickets picked yet.</p>
            ) : (
              <ul className="product-picks-lines">
                {pickLines.map((line) => (
                  <li key={line.key} className="product-picks-line">
                    <span>{line.words}</span>
                    {line.amount !== undefined ? <span className="product-picks-amount">{line.amount}</span> : null}
                  </li>
                ))}
              </ul>
            )}
            {total !== undefined && ticketCount > 0 ? <TotalRow label="Total" amount={formatMoney(total)} /> : null}
          </section>
        </SheetCard>
        )}
      </aside>

      {interrupt ? (
        props.foot === "sheet" ? (
          <SheetFoot
            pinned
            redline="embed-group-interrupt-footer"
            totalRedline="embed-group-interrupt-footer-total"
            subRedline="embed-group-interrupt-footer-line"
            {...(total !== undefined ? { total: formatMoney(total) } : {})}
            sub={groupFooterLine(party, ticketCount)}
          >
            {props.onKeepTickets !== undefined ? (
              <Button variant="textlink-accent" data={{ redline: "embed-group-interrupt-keep-tickets" }} onClick={props.onKeepTickets}>{keepLabel(kept)}</Button>
            ) : null}
            <Button size="lg" busy={props.busy === true} onClick={() => props.onRequestGroupQuote?.()} data={{ redline: "embed-group-interrupt-cta" }}>
              <span data-redline="embed-group-interrupt-cta-text">REQUEST A GROUP QUOTE</span>
            </Button>
          </SheetFoot>
        ) : (
          <StickyBar redline="tickets-sticky" seatedWhenWide summary={groupFooterLine(party, ticketCount)}>
            <>
              {props.onKeepTickets !== undefined ? (
                <Button variant="textlink-accent" data={{ redline: "embed-group-interrupt-keep-tickets" }} onClick={props.onKeepTickets}>{keepLabel(kept)}</Button>
              ) : null}
              <Button size="lg" busy={props.busy === true} onClick={() => props.onRequestGroupQuote?.()} data={{ redline: "embed-group-interrupt-cta" }}>
                <span data-redline="embed-group-interrupt-cta-text">REQUEST A GROUP QUOTE</span>
              </Button>
            </>
          </StickyBar>
        )
      ) : props.foot === "sheet" ? (
        /* B523 (368-2) — the sheet's foot: the number LEFT, the door RIGHT. */
        <SheetFoot
          pinned
          redline="tickets-sticky"
          {...(total !== undefined ? { total: formatMoney(total) } : {})}
          {...(summary !== undefined && summary !== "" ? { sub: summary } : {})}
        >
          <Button
            size="lg"
            disabled={gated}
            busy={props.busy === true}
            {...(gated && issues.length > 0 ? { describedBy: "tickets-issues" } : {})}
            onClick={props.onContinue}
          >
            Continue →
          </Button>
        </SheetFoot>
      ) : (
      <StickyContinue
        {...(summary !== undefined && summary !== "" ? { summary } : {})}
        gated={gated}
        describedBy={issues.length > 0 ? "tickets-issues" : undefined}
        busy={props.busy === true}
        label={total !== undefined ? `Continue to cart · ${formatMoney(total)}` : "Continue to cart"}
        onContinue={props.onContinue}
      />
      )}
    </div>
  );
}

/** The bar split out so the gate/label derivation above stays readable. */
function StickyContinue(props: {
  readonly summary?: string;
  readonly gated: boolean;
  readonly describedBy?: string | undefined;
  readonly busy: boolean;
  readonly label: string;
  readonly onContinue: () => void;
}): ReactElement {
  return (
    <StickyBarShim summary={props.summary}>
      <Button
        size="lg"
        disabled={props.gated}
        busy={props.busy}
        {...(props.gated && props.describedBy !== undefined ? { describedBy: props.describedBy } : {})}
        onClick={props.onContinue}
      >
        {props.label}
      </Button>
    </StickyBarShim>
  );
}

function StickyBarShim(props: { readonly summary?: string | undefined; readonly children: ReactElement }): ReactElement {
  return (
    <StickyBar redline="tickets-sticky" seatedWhenWide {...(props.summary !== undefined ? { summary: props.summary } : {})}>
      {props.children}
    </StickyBar>
  ) as ReactElement;
}
